import { DateFormatter } from '@repo/toolkit/date'

import i18n from '../../../../../i18n'
import { getTranslatedName } from '../../../utils/functions/translation.functions'
import { Formula } from '../formula/formula'
import { Option } from '../options/option'
import { TripProperties } from '../trip/properties.trip'
import { CenterToJourney } from './center-to-journey'
import { CenterToJourneyInput, CenterToJourneyRequestInput } from './center-to-journey-input'

export class CenterToJourneyProperties {
  /**
   *  Get the total number of children based on the max number of student places
   * @param data  CenterToJourneyInput[]
   * @returns   number
   */
  static getTotalNumberOfChildren(data?: CenterToJourneyInput[]) {
    if (!data) return 0
    return data?.reduce(
      (acc, centerToJourney) => acc + Number(centerToJourney.maxNumberOfStudentPlaces),
      0
    )
  }

  /**
   * Check if the trips and the number of children are empty
   * @param centerToJourneys CenterToJourneyInput[]
   * @returns string | undefined
   */
  static checkIfTripsAndNumberOfChildrenAreEmpty(centerToJourneys: CenterToJourneyInput[]) {
    const trips = centerToJourneys.map((centerToJourney) => centerToJourney.relatedTrips)

    if (
      trips.some((trip) => trip.length === 0) ||
      centerToJourneys.some((centerToJourney) => centerToJourney.maxNumberOfStudentPlaces === 0)
    ) {
      return 'Veuillez sélectionner les voyages et le nombre de jeunes pour chaque séjour'
    }
  }

  static getCountryCode(centerToJourney: CenterToJourneyInput): string {
    return centerToJourney.journey.date.product.country.code
  }

  static getTripDate(centerToJourney: CenterToJourneyInput): string {
    const { departureDate, returnDate } = centerToJourney.journey.date
    return `${DateFormatter.DDMMYYYY(departureDate)} - ${DateFormatter.DDMMYYYY(returnDate)}`
  }

  static getFormulaName(centerToJourney: {
    journey: { date: { product: { formula: Pick<Formula, 'translations'> } } }
  }): string {
    return getTranslatedName(centerToJourney.journey.date.product.formula, i18n.language) || '—'
  }

  static getThemeName(centerToJourney: {
    journey: { mainOption: Pick<Option, 'translations'> }
  }): string {
    return getTranslatedName(centerToJourney.journey.mainOption, i18n.language) || '—'
  }

  static getOptions(centerToJourney: {
    journey: {
      secondaryOptions: { option: Pick<Option, 'translations'> }[]
    }
  }): string[] {
    return centerToJourney.journey.secondaryOptions.map(
      (secondaryOption) => getTranslatedName(secondaryOption.option, i18n.language) || '—'
    )
  }

  static getMainTrips(centerToJourney: CenterToJourneyInput): string[] {
    return centerToJourney.relatedTrips.map((trip) => TripProperties.getTripLabel(trip))
  }

  static getSecondaryTripsData(centerToJourney: CenterToJourneyInput) {
    const mainTrips = centerToJourney.relatedTrips.map((trip) => trip.id)

    return centerToJourney.journey.places
      .filter((place) => mainTrips.includes(place.mainTrip.id))
      .map((place) => place.secondaryTrip)
      .filter((secondaryTrip) => secondaryTrip)
  }

  static getSecondaryTrips(centerToJourney: CenterToJourneyInput): string[] {
    return this.getSecondaryTripsData(centerToJourney).map((trip) =>
      TripProperties.getTripLabel(trip)
    )
  }

  static getMaxNumberOfParticipants(centerToJourney: CenterToJourneyInput): number {
    return centerToJourney.maxNumberOfStudentPlaces
  }

  static mapCenterToJourneyData(centerToJourney: CenterToJourney) {
    return {
      countryCode: this.getCountryCode(centerToJourney),
      tripDate: this.getTripDate(centerToJourney),
      formulaName: this.getFormulaName(centerToJourney),
      themeName: this.getThemeName(centerToJourney),
      options: this.getOptions(centerToJourney),
      mainTrips: this.getMainTrips(centerToJourney),
      secondaryTrip: '-',
      maxNumberOfParticipants: this.getMaxNumberOfParticipants(centerToJourney)
    }
  }

  static mapCenterToJourneyInputToRequest(
    centerToJourney: CenterToJourneyInput[]
  ): CenterToJourneyRequestInput[] {
    return centerToJourney.map((centerToJourney) => ({
      journeyId: centerToJourney.journey.id,
      maxNumberOfStudentPlaces: centerToJourney.maxNumberOfStudentPlaces,
      relatedTrips: centerToJourney.relatedTrips.map((trip) => trip.id)
    }))
  }
}
